import React from 'react';
import { Info, AlertTriangle, AlertCircle, Clock, CheckCircle, type LucideIcon } from 'lucide-react';
import type { TwinEvent, EventSeverity } from '../../types';
import { EventSeverityBadge } from '../../atoms/EventSeverityBadge';
import './TwinEventCard.css';

export interface TwinEventCardProps {
  /** The event to display */
  event: TwinEvent;
  /** Callback when contract chip is clicked */
  onContractClick?: (contractNumber: string) => void;
  /** Callback when Bestätigen is clicked */
  onAcknowledge?: (eventId: string) => void;
  /** Callback when Zurückstellen is clicked */
  onDismiss?: (eventId: string) => void;
  /** Callback when Erledigt is clicked */
  onResolve?: (eventId: string) => void;
  /**
   * Opens the object the event is about (FR-017 #7b). Pass it only when the
   * caller can route to that object; without it no "Zum Objekt" link shows.
   */
  onOpenObject?: (event: TwinEvent) => void;
  /** Optional custom className */
  className?: string;
}

const SEVERITY_ICON: Record<EventSeverity, LucideIcon> = {
  info: Info,
  warning: AlertTriangle,
  action_required: AlertCircle,
};

function formatTimeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 30) return `vor ${Math.floor(days / 30)} Mon.`;
  if (days > 0) return `vor ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
  if (hours > 0) return `vor ${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`;
  return 'gerade eben';
}

export const TwinEventCard: React.FC<TwinEventCardProps> = ({
  event,
  onContractClick,
  onAcknowledge,
  onDismiss,
  onResolve,
  onOpenObject,
  className = '',
}) => {
  const Icon = SEVERITY_ICON[event.severity] ?? Info;
  const isResolved = event.status === 'resolved';
  const isDismissed = event.status === 'dismissed';

  return (
    <div
      className={`twin-event-card twin-event-card--${event.severity} ${isResolved ? 'twin-event-card--resolved' : ''} ${className}`.trim()}
    >
      <div className="twin-event-card__icon">
        {isResolved ? <CheckCircle size={20} /> : <Icon size={20} />}
      </div>
      <div className="twin-event-card__content">
        <div className="twin-event-card__header">
          <span className="twin-event-card__title">{event.explanation}</span>
          <span className="twin-event-card__time">
            <Clock size={12} />
            {formatTimeAgo(event.created_at)}
          </span>
        </div>

        <div className="twin-event-card__meta">
          <EventSeverityBadge severity={event.severity} />
          {event.contract_number && (
            <button
              className="twin-event-card__chip"
              onClick={(e) => {
                e.stopPropagation();
                onContractClick?.(event.contract_number!);
              }}
            >
              {event.contract_number}
            </button>
          )}
          {event.supplier_name && (
            <span className="twin-event-card__chip twin-event-card__chip--supplier">
              {event.supplier_name}
            </span>
          )}
          {onOpenObject && (
            <button
              type="button"
              className="twin-event-card__chip twin-event-card__chip--object"
              onClick={(e) => {
                e.stopPropagation();
                onOpenObject(event);
              }}
            >
              Zum Objekt
            </button>
          )}
        </div>

        {!isResolved && !isDismissed && (
          <div className="twin-event-card__actions">
            {event.severity === 'action_required' && onResolve && (
              <button
                className="twin-event-card__action twin-event-card__action--primary"
                onClick={() => onResolve(event.event_id)}
              >
                Erledigt
              </button>
            )}
            {onAcknowledge && (
              <button
                className="twin-event-card__action"
                onClick={() => onAcknowledge(event.event_id)}
              >
                Bestätigen
              </button>
            )}
            {onDismiss && (
              <button
                className="twin-event-card__action"
                onClick={() => onDismiss(event.event_id)}
              >
                Zurückstellen
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TwinEventCard;
